import { useEffect, useState } from 'preact/hooks';
import { NewMark } from '../releases.js';
import { ProjectBrandStrip } from './brand.js';
import type { Api } from '../api.js';
import { when } from '../format.js';
import { texts, useLang } from '../i18n.js';
import { Dialog, LoadError, Loading, Pill, toast } from '../ui.js';

/**
 * The project brief (owner decision 2026-10-15): the project part of the brief, written on the project and sent to one
 * client contact, who approves it or asks for changes in the client portal. Send emails the contact's personal portal
 * link from the Hub's mailbox (hub@); where the Hub cannot email (no sender, or staging and a client domain) the
 * producer gets a ready email text to copy into their own mail instead.
 */

type Status = 'draft' | 'sent' | 'approved' | 'changes_requested';
export interface ProjectBriefView {
  sections: { key: string; label: string; hint: string; text: string }[];
  version: number;
  updatedBy: string | null;
  updatedAt: string | null;
  status: Status;
  statusText: string;
  changedSinceSent: boolean;
  sent: { at: string; by: string; to: string; toId: string | null; version: number | null } | null;
  decision: { at: string; by: string; comment: string | null } | null;
  recipients: { id: string; name: string | null; email: string; canApprove: boolean; byEmail: boolean }[];
  suggested: string | null;
  history: { id: string; kind: 'sent' | 'approved' | 'changes_requested'; at: string; version: number; text: string; comment: string | null }[];
  canEdit: boolean;
  canSend: boolean;
}
interface SentMail {
  to: string;
  name: string | null;
  lang: 'sv' | 'en';
  subject: string;
  text: string;
}

const TX = texts(
  {
    copiedMail: 'Email text copied',
    copiedSubject: 'Subject copied',
    copyFailedMail: 'Could not copy – select the email text and copy it by hand',
    copyFailedSubject: 'Could not copy – select the subject and copy it by hand',
    notSent: 'Not sent to the client yet.',
    status: { draft: 'Not sent yet', sent: 'Waiting for the client', approved: 'Approved by the client', changes_requested: 'The client asked for changes' } as Record<Status, string>,
    sec: {} as Record<string, [string, string]>,
    sentTo: (to: string, at: string, v: number | null) => `Sent to ${to} ${at} (version ${v}) – waiting for their approval.`,
    approved: (by: string, v: number | undefined | null, at: string) => `${by} approved version ${v} ${at}.`,
    askedChanges: (by: string, v: number | undefined | null, at: string) => `${by} asked for changes to version ${v} ${at}.`,
    changedSince: ' You have changed it since – the client sees the sent version until you send it again.',
    mailTitle: 'Send the email to the client',
    openMail: 'Open in your email',
    copyMail: 'Copy email text',
    subject: 'Subject',
    copy: 'Copy',
    waitingFor: (who: string) => `The brief is waiting for ${who} in the portal. Copy this into your own email and send it to `,
    noSender: 'The Hub cannot send email here.',
    emailedTitle: 'The brief is on its way',
    emailed: (who: string) => `The Hub emailed the brief to ${who} `,
    emailedTail: ' with their personal portal link. Replies come to you.',
    copyLink: 'Copy the link',
    copiedLink: 'Link copied',
    copyFailedLink: 'Could not copy the link',
    done: 'Done',
    byEmail: 'The Hub emails it to them from its own mailbox, and replies come to you.',
    byCopy: 'The Hub cannot email them here – you copy the email text next.',
    mailLang: (sv: boolean) => `Email text (${sv ? 'Swedish' : 'English'}, the client's language)`,
    sendAgainTitle: 'Send the brief again',
    sendTitle: 'Send the brief for approval',
    cancel: 'Cancel',
    sending: 'Sending…',
    sendForApproval: 'Send for approval',
    whoApproves: 'Who at the client approves it?',
    approves: 'Approves',
    gets: (who: string | null) => (who ? `${who} gets` : 'They get'),
    linkHint: (v: number) => ` a new personal portal link, and their older portal link stops working. They see version ${v} of the brief and press Approve or Ask for changes. `,
    noContacts: 'The client has no contacts yet. Add one under Details › Contacts first.',
    savedAgain: (v: number) => `Saved as version ${v} – send it again for the client to approve the change`,
    saved: (v: number) => `Brief saved (version ${v})`,
    clientSees: (v: number | null) => `The client sees version ${v} until you send the brief again.`,
    saving: 'Saving…',
    saveBrief: 'Save brief',
    sendAgainBtn: 'Send again…',
    sendBtn: 'Send for approval…',
    projectBrief: 'Project brief',
    edit: 'Edit',
    write: 'Write the brief',
    wrote: (by: string) => `${by} wrote:`,
    version: (v: number) => `Version ${v}`,
    someone: 'Someone',
    empty: 'No project brief yet. It says what the project is – goal, audience, message, videos, tone, shoot and delivery – and the client approves it in the portal. The internal client brief stays on the client card and is never sent.',
    history: (n: number) => `History (${n})`,
    draftBtn: 'Draft from meetings…',
    draftTitle: 'Draft the brief with Klara',
    draftFrom: (source: string) => `Klara reads the meetings you tick in ${source}, plus the project's videos, dates and shoots. Prices and contact details are left out.`,
    noSource: "No notetaker is connected, so Klara drafts from the project's videos, dates and shoots only.",
    noMeetings: (source: string) => `No meetings with this client in ${source} in the last four months. Klara drafts from the project's videos, dates and shoots only.`,
    byDomain: 'colleague at the client',
    byTitle: 'client named in the title',
    byCompany: "participant from the client's company",
    openMeeting: 'Open',
    loadingMeetings: 'Fetching the meetings…',
    writeDraft: 'Write draft',
    klaraWriting: 'Klara is writing…',
    tooMany: (n: number) => `Tick at most ${n} meetings.`,
    filled: (n: number, kept: number) =>
      `Klara filled ${n} ${n === 1 ? 'heading' : 'headings'}${kept ? ` and left the ${kept} you had already written alone` : ''}. Read it, fix what is wrong, then save.`,
    nothingFilled: 'Every heading Klara wrote already has your text, so nothing changed. Empty a heading to let Klara fill it.'
  },
  {
    copiedMail: 'Mejltexten kopierad',
    copiedSubject: 'Ämnet kopierat',
    copyFailedMail: 'Det gick inte att kopiera – markera mejltexten och kopiera den för hand',
    copyFailedSubject: 'Det gick inte att kopiera – markera ämnet och kopiera det för hand',
    notSent: 'Inte skickad till kunden än.',
    status: { draft: 'Inte skickad', sent: 'Väntar på kunden', approved: 'Godkänd av kunden', changes_requested: 'Kunden vill ändra' } as Record<Status, string>,
    sec: {
      goal: ['Mål', 'Vad ska filmerna åstadkomma?'],
      audience: ['Målgrupp', 'Vem är de till för?'],
      message: ['Budskap', 'Vad ska tittaren komma ihåg?'],
      videos: ['Videor och format', 'Vilka videor, längder, format, undertexter och språk.'],
      tone: ['Ton och referenser', 'Hur ska det kännas? Länkar till referenser.'],
      shoot: ['Inspelning', 'Datum, tid, plats och kontakt på plats.'],
      delivery: ['Leverans', 'När och var filmerna levereras.']
    } as Record<string, [string, string]>,
    sentTo: (to: string, at: string, v: number | null) => `Skickad till ${to} ${at} (version ${v}) – väntar på godkännande.`,
    approved: (by: string, v: number | undefined | null, at: string) => `${by} godkände version ${v} ${at}.`,
    askedChanges: (by: string, v: number | undefined | null, at: string) => `${by} önskade ändringar i version ${v} ${at}.`,
    changedSince: ' Du har ändrat den sedan dess – kunden ser den skickade versionen tills du skickar igen.',
    mailTitle: 'Skicka mejlet till kunden',
    openMail: 'Öppna i din mejl',
    copyMail: 'Kopiera mejltexten',
    subject: 'Ämne',
    copy: 'Kopiera',
    waitingFor: (who: string) => `Briefen väntar på ${who} i portalen. Kopiera det här till din egen mejl och skicka till `,
    noSender: 'Hubben kan inte skicka mejl här.',
    emailedTitle: 'Briefen är skickad',
    emailed: (who: string) => `Hubben mejlade briefen till ${who} `,
    emailedTail: ' med en personlig portallänk. Svar kommer till dig.',
    copyLink: 'Kopiera länken',
    copiedLink: 'Länken kopierad',
    copyFailedLink: 'Det gick inte att kopiera länken',
    done: 'Klar',
    byEmail: 'Hubben mejlar den till dem från sin egen brevlåda, och svar kommer till dig.',
    byCopy: 'Hubben kan inte mejla dem här – du kopierar mejltexten i nästa steg.',
    mailLang: (sv: boolean) => `Mejltext (${sv ? 'svenska' : 'engelska'}, kundens språk)`,
    sendAgainTitle: 'Skicka briefen igen',
    sendTitle: 'Skicka briefen för godkännande',
    cancel: 'Avbryt',
    sending: 'Skickar…',
    sendForApproval: 'Skicka för godkännande',
    whoApproves: 'Vem hos kunden godkänner den?',
    approves: 'Godkänner',
    gets: (who: string | null) => (who ? `${who} får` : 'De får'),
    linkHint: (v: number) => ` en ny personlig portallänk, och den äldre portallänken slutar fungera. De ser version ${v} av briefen och trycker Godkänn eller Be om ändringar. `,
    noContacts: 'Kunden har inga kontakter än. Lägg till en under Detaljer › Kontakter först.',
    savedAgain: (v: number) => `Sparad som version ${v} – skicka igen så att kunden kan godkänna ändringen`,
    saved: (v: number) => `Briefen sparad (version ${v})`,
    clientSees: (v: number | null) => `Kunden ser version ${v} tills du skickar briefen igen.`,
    saving: 'Sparar…',
    saveBrief: 'Spara brief',
    sendAgainBtn: 'Skicka igen…',
    sendBtn: 'Skicka för godkännande…',
    projectBrief: 'Projektbrief',
    edit: 'Ändra',
    write: 'Skriv briefen',
    wrote: (by: string) => `${by} skrev:`,
    version: (v: number) => `Version ${v}`,
    someone: 'Någon',
    empty: 'Ingen projektbrief än. Den beskriver projektet – mål, målgrupp, budskap, videor, ton, inspelning och leverans – och kunden godkänner den i portalen. Den interna kundbriefen ligger kvar på kundkortet och skickas aldrig.',
    history: (n: number) => `Historik (${n})`,
    draftBtn: 'Utkast från möten…',
    draftTitle: 'Skriv briefen med Klara',
    draftFrom: (source: string) => `Klara läser mötena du bockar i från ${source}, plus projektets videor, datum och inspelningar. Priser och kontaktuppgifter tas bort.`,
    noSource: 'Ingen notetaker är ansluten, så Klara skriver utkastet utifrån projektets videor, datum och inspelningar.',
    noMeetings: (source: string) => `Inga möten med den här kunden i ${source} de senaste fyra månaderna. Klara skriver utkastet utifrån projektets videor, datum och inspelningar.`,
    byDomain: 'kollega hos kunden',
    byTitle: 'kunden nämns i rubriken',
    byCompany: 'deltagare från kundens företag',
    openMeeting: 'Öppna',
    loadingMeetings: 'Hämtar mötena…',
    writeDraft: 'Skriv utkast',
    klaraWriting: 'Klara skriver…',
    tooMany: (n: number) => `Bocka i högst ${n} möten.`,
    filled: (n: number, kept: number) =>
      `Klara fyllde ${n} ${n === 1 ? 'rubrik' : 'rubriker'}${kept ? ` och lät de ${kept} du redan skrivit vara` : ''}. Läs igenom, rätta det som är fel och spara sedan.`,
    nothingFilled: 'Alla rubriker Klara skrev har redan din text, så inget ändrades. Töm en rubrik om du vill att Klara fyller den.'
  }
);
const TONE: Record<Status, 'ok' | 'info' | 'warn' | 'neutral'> = { draft: 'neutral', sent: 'info', approved: 'ok', changes_requested: 'warn' };
const errText = (e: unknown) => (e instanceof Error ? e.message : String(e));
const val = (e: Event) => (e.target as HTMLInputElement | HTMLTextAreaElement).value;

async function copy(text: string, ok: string, failed: string) {
  try {
    await navigator.clipboard.writeText(text);
    toast({ text: ok });
  } catch {
    toast({ text: failed });
  }
}

/** The line under the heading: where the brief is with the client. */
function StatusLine(p: { b: ProjectBriefView }) {
  const { b } = p;
  const lang = useLang();
  const t = TX[lang];
  const at = (iso: string) => when(iso, lang);
  if (b.status === 'draft') return <p class="muted">{t.notSent}</p>;
  return (
    <p class="muted">
      {b.status === 'sent' && b.sent
        ? t.sentTo(b.sent.to, at(b.sent.at), b.sent.version)
        : b.status === 'approved' && b.decision
          ? t.approved(b.decision.by, b.sent?.version, at(b.decision.at))
          : b.decision
            ? t.askedChanges(b.decision.by, b.sent?.version, at(b.decision.at))
            : t.status[b.status]}
      {b.changedSinceSent ? t.changedSince : ''}
    </p>
  );
}

function SendDialog(p: { api: Api; id: string; b: ProjectBriefView; onSent: (b: ProjectBriefView) => void; onClose: () => void }) {
  const t = TX[useLang()];
  const [to, setTo] = useState<string | null>(p.b.suggested);
  const [busy, setBusy] = useState(false);
  const [mail, setMail] = useState<SentMail | null>(null);
  const [emailed, setEmailed] = useState(false);
  const [notEmailed, setNotEmailed] = useState<string | null>(null);
  const [url, setUrl] = useState('');
  const who = p.b.recipients.find((r) => r.id === to);
  const send = async () => {
    if (!to) return;
    setBusy(true);
    try {
      const r = await p.api.post<{ brief: ProjectBriefView; email: SentMail; url: string; emailed: boolean; notEmailed: string | null }>(`/api/projects/${p.id}/brief/send`, { contactId: to });
      p.onSent(r.brief);
      setEmailed(r.emailed);
      setNotEmailed(r.notEmailed);
      setUrl(r.url);
      setMail(r.email);
    } catch (e) {
      toast({ text: errText(e) });
    } finally {
      setBusy(false);
    }
  };
  if (mail && emailed)
    return (
      <Dialog
        title={t.emailedTitle}
        onClose={p.onClose}
        foot={
          <>
            <button type="button" class="btn" onClick={() => void copy(url, t.copiedLink, t.copyFailedLink)}>
              {t.copyLink}
            </button>
            <button type="button" class="btn btn-primary" onClick={p.onClose}>
              {t.done}
            </button>
          </>
        }
      >
        <p class="pb-emailed">
          {t.emailed(mail.name ?? mail.to)}(<strong>{mail.to}</strong>){t.emailedTail}
        </p>
      </Dialog>
    );
  if (mail)
    return (
      <Dialog
        title={t.mailTitle}
        onClose={p.onClose}
        foot={
          <>
            <a class="btn" href={`mailto:${encodeURIComponent(mail.to)}?subject=${encodeURIComponent(mail.subject)}&body=${encodeURIComponent(mail.text)}`}>
              {t.openMail}
            </a>
            <button type="button" class="btn btn-primary" onClick={() => void copy(mail.text, t.copiedMail, t.copyFailedMail)}>
              {t.copyMail}
            </button>
          </>
        }
      >
        <div class="stack">
          <p class="callout callout--warn pb-not-emailed" role="status">
            {notEmailed ?? t.noSender}
          </p>
          <p>
            {t.waitingFor(mail.name ?? mail.to)}
            <strong>{mail.to}</strong>.
          </p>
          <label class="field">
            <span class="label">{t.subject}</span>
            <span class="cluster">
              <input class="input input--wide pb-subject" readOnly value={mail.subject} onFocus={(e) => (e.target as HTMLInputElement).select()} />
              <button type="button" class="btn btn-sm" onClick={() => void copy(mail.subject, t.copiedSubject, t.copyFailedSubject)}>
                {t.copy}
              </button>
            </span>
          </label>
          <label class="field">
            <span class="label">{t.mailLang(mail.lang === 'sv')}</span>
            <textarea class="textarea pb-mail" readOnly value={mail.text} onFocus={(e) => (e.target as HTMLTextAreaElement).select()} />
          </label>
        </div>
      </Dialog>
    );
  return (
    <Dialog
      title={p.b.sent ? t.sendAgainTitle : t.sendTitle}
      onClose={p.onClose}
      foot={
        <>
          <button type="button" class="btn" onClick={p.onClose}>
            {t.cancel}
          </button>
          <button type="button" class="btn btn-primary" disabled={!to || busy} onClick={() => void send()}>
            {busy ? t.sending : t.sendForApproval}
          </button>
        </>
      }
    >
      {p.b.recipients.length ? (
        <div class="stack">
          <fieldset class="fieldset">
            <legend class="label">{t.whoApproves}</legend>
            {p.b.recipients.map((r) => (
              <label class="check">
                <input type="radio" name="pb-to" checked={to === r.id} onChange={() => setTo(r.id)} />
                <span>
                  {r.name ?? r.email} <span class="muted small">{r.name ? r.email : ''}</span> {r.canApprove ? <Pill tone="info">{t.approves}</Pill> : null}
                </span>
              </label>
            ))}
          </fieldset>
          <p class="hint">
            {t.gets(who ? (who.name ?? who.email) : null)}
            {t.linkHint(p.b.version)}
            {who?.byEmail ? t.byEmail : t.byCopy}
          </p>
        </div>
      ) : (
        <p>{t.noContacts}</p>
      )}
    </Dialog>
  );
}

interface MeetingsView {
  source: string | null;
  meetings: { id: string; title: string; startAt: string; gist: string | null; url: string | null; matchedBy: 'client' | 'contact' | 'domain' | 'company' | 'title' }[];
  suggested: string[];
}
const DRAFT_MEETINGS_MAX = 5;

/** Klara drafts the brief from the client's meetings in the notetaker (owner 2026-10-27); the editor keeps what is written. */
function DraftDialog(p: { api: Api; id: string; onDraft: (sections: Record<string, string>) => void; onClose: () => void }) {
  const lang = useLang();
  const t = TX[lang];
  const [v, setV] = useState<MeetingsView | null>(null);
  const [err, setErr] = useState<unknown>(null);
  const [picked, setPicked] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    p.api.get<MeetingsView>(`/api/projects/${p.id}/brief/meetings`).then((r) => (setV(r), setPicked(r.suggested)), setErr);
  }, [p.id]);
  const flip = (id: string) => setPicked(picked.includes(id) ? picked.filter((x) => x !== id) : [...picked, id]);
  const tooMany = picked.length > DRAFT_MEETINGS_MAX;
  const draft = async () => {
    setBusy(true);
    try {
      const r = await p.api.post<{ sections: { key: string; text: string }[] }>(`/api/projects/${p.id}/brief/draft`, { meetingIds: picked });
      p.onDraft(Object.fromEntries(r.sections.map((s) => [s.key, s.text])));
      p.onClose();
    } catch (e) {
      toast({ text: errText(e) });
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      title={t.draftTitle}
      onClose={p.onClose}
      foot={
        <>
          <button type="button" class="btn" onClick={p.onClose}>
            {t.cancel}
          </button>
          <button type="button" class="btn btn-primary" disabled={!v || busy || tooMany} onClick={() => void draft()}>
            {busy ? t.klaraWriting : t.writeDraft}
          </button>
        </>
      }
    >
      {err ? (
        <LoadError error={err} />
      ) : !v ? (
        <p class="muted">{t.loadingMeetings}</p>
      ) : !v.source ? (
        <p>{t.noSource}</p>
      ) : !v.meetings.length ? (
        <p>{t.noMeetings(v.source)}</p>
      ) : (
        <div class="stack">
          <p class="hint">{t.draftFrom(v.source)}</p>
          <fieldset class="fieldset pb-meetings">
            {v.meetings.map((m) => (
              <label class="check">
                <input type="checkbox" checked={picked.includes(m.id)} onChange={() => flip(m.id)} />
                <span>
                  <strong>{m.title}</strong> <span class="muted small">{when(m.startAt, lang)}</span>
                  {m.matchedBy === 'domain' || m.matchedBy === 'company' || m.matchedBy === 'title' ? (
                    <span class="muted small"> · {m.matchedBy === 'domain' ? t.byDomain : m.matchedBy === 'company' ? t.byCompany : t.byTitle}</span>
                  ) : null}
                  {m.url ? (
                    <>
                      {' '}
                      <a class="small" href={m.url} target="_blank" rel="noopener noreferrer">
                        {t.openMeeting}
                      </a>
                    </>
                  ) : null}
                  {m.gist ? <span class="small muted pb-gist">{m.gist}</span> : null}
                </span>
              </label>
            ))}
          </fieldset>
          {tooMany ? <p class="callout callout--warn">{t.tooMany(DRAFT_MEETINGS_MAX)}</p> : null}
        </div>
      )}
    </Dialog>
  );
}

function BriefEditor(p: { api: Api; id: string; b: ProjectBriefView; startWithDraft?: boolean; onSaved: (b: ProjectBriefView) => void; onCancel: () => void }) {
  const t = TX[useLang()];
  const [text, setText] = useState<Record<string, string>>(() => Object.fromEntries(p.b.sections.map((s) => [s.key, s.text])));
  const [busy, setBusy] = useState(false);
  const [drafting, setDrafting] = useState(Boolean(p.startWithDraft));
  const [note, setNote] = useState('');
  // Klara only fills the headings that are still empty; what the producer wrote stays
  const takeDraft = (draft: Record<string, string>) => {
    const fill = Object.keys(draft).filter((k) => draft[k]?.trim() && !text[k]?.trim());
    const kept = Object.keys(draft).filter((k) => draft[k]?.trim() && text[k]?.trim()).length;
    setText({ ...text, ...Object.fromEntries(fill.map((k) => [k, draft[k]!])) });
    setNote(fill.length ? t.filled(fill.length, kept) : t.nothingFilled);
  };
  const save = async () => {
    setBusy(true);
    try {
      const r = await p.api.put<{ brief: ProjectBriefView }>(`/api/projects/${p.id}/brief`, { sections: text });
      p.onSaved(r.brief);
      toast({ text: r.brief.sent ? t.savedAgain(r.brief.version) : t.saved(r.brief.version) });
    } catch (e) {
      toast({ text: errText(e) });
    } finally {
      setBusy(false);
    }
  };
  return (
    <form
      class="stack"
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      {p.b.sent ? <p class="hint">{t.clientSees(p.b.sent.version)}</p> : null}
      <div class="cluster">
        <button type="button" class="btn btn-sm" disabled={busy} onClick={() => setDrafting(true)}>
          {t.draftBtn}
          <NewMark k="brief-from-meetings" />
        </button>
      </div>
      {note ? (
        <p class="small muted" role="status">
          {note}
        </p>
      ) : null}
      {p.b.sections.map((s) => (
        <label class="field">
          <span class="label">{t.sec[s.key]?.[0] ?? s.label}</span>
          <span class="hint">{t.sec[s.key]?.[1] ?? s.hint}</span>
          <textarea class="textarea pb-input" value={text[s.key] ?? ''} maxLength={4000} onInput={(e) => setText({ ...text, [s.key]: val(e) })} />
        </label>
      ))}
      <div class="cluster">
        <button type="submit" class="btn btn-primary" disabled={busy}>
          {busy ? t.saving : t.saveBrief}
        </button>
        <button type="button" class="btn" onClick={p.onCancel}>
          {t.cancel}
        </button>
      </div>
      {drafting ? <DraftDialog api={p.api} id={p.id} onDraft={takeDraft} onClose={() => setDrafting(false)} /> : null}
    </form>
  );
}

export function ProjectBriefTab(p: { api: Api; id: string; crew?: boolean }) {
  const lang = useLang();
  const t = TX[lang];
  const [b, setB] = useState<ProjectBriefView | null>(null);
  const [err, setErr] = useState<unknown>(null);
  const [editing, setEditing] = useState<false | 'write' | 'draft'>(false);
  const [sending, setSending] = useState(false);
  useEffect(() => {
    p.api.get<{ brief: ProjectBriefView }>(`/api/projects/${p.id}/brief`).then((r) => setB(r.brief), setErr);
  }, [p.id]);
  // the client's brand first: the whole team sees it, even where the brief itself is not theirs to read
  const brand = <ProjectBrandStrip api={p.api} projectId={p.id} staff={!p.crew} />;
  if (err)
    return (
      <div class="stack pb">
        {brand}
        <LoadError error={err} />
      </div>
    );
  if (!b) return <Loading />;
  const filled = b.sections.filter((s) => s.text.trim());
  const sendLabel = b.sent ? t.sendAgainBtn : t.sendBtn;
  return (
    <div class="stack pb">
      {brand}
      <section class="blk" aria-labelledby="pb-h">
        <div class="blk-head">
          <h2 class="blk-title" id="pb-h">
            {t.projectBrief} {b.status === 'draft' ? null : <Pill tone={TONE[b.status]}>{t.status[b.status]}</Pill>}
          </h2>
          {!editing && b.canEdit ? (
            <div class="blk-actions cluster">
              {filled.length ? null : (
                <button type="button" class="btn btn-sm" onClick={() => setEditing('draft')}>
                  {t.draftBtn}
                  <NewMark k="brief-from-meetings" />
                </button>
              )}
              <button type="button" class={b.canSend ? 'btn btn-sm' : 'btn btn-sm btn-primary'} onClick={() => setEditing('write')}>
                {filled.length ? t.edit : t.write}
              </button>
              {b.canSend ? (
                <button type="button" class="btn btn-sm btn-primary" onClick={() => setSending(true)}>
                  {sendLabel}
                </button>
              ) : null}
            </div>
          ) : null}
        </div>
        <StatusLine b={b} />
        {b.status === 'changes_requested' && b.decision?.comment ? (
          <div class="callout callout--warn pb-comment" role="status">
            <strong>{t.wrote(b.decision.by)}</strong>
            <div class="brief-body">{b.decision.comment}</div>
          </div>
        ) : null}
        {editing ? (
          <BriefEditor
            api={p.api}
            id={p.id}
            b={b}
            startWithDraft={editing === 'draft'}
            onSaved={(n) => {
              setB(n);
              setEditing(false);
            }}
            onCancel={() => setEditing(false)}
          />
        ) : filled.length ? (
          <div class="pb-sections">
            {filled.map((s) => (
              <section>
                <h3 class="small-head">{t.sec[s.key]?.[0] ?? s.label}</h3>
                <div class="brief-body">{s.text}</div>
              </section>
            ))}
            {b.updatedAt ? (
              <p class="small muted">
                {t.version(b.version)} · {b.updatedBy ?? t.someone} · {when(b.updatedAt, lang)}
              </p>
            ) : null}
          </div>
        ) : (
          <p class="muted">{t.empty}</p>
        )}
      </section>
      {b.history.length ? (
        <details class="blk pb-history">
          <summary class="blk-title">{t.history(b.history.length)}</summary>
          <ul class="pb-events">
            {b.history.map((e) => (
              <li>
                <span>{e.text}</span> <span class="muted small">{when(e.at, lang)}</span>
                {e.comment ? <div class="brief-body small">“{e.comment}”</div> : null}
              </li>
            ))}
          </ul>
        </details>
      ) : null}
      {sending ? <SendDialog api={p.api} id={p.id} b={b} onSent={setB} onClose={() => setSending(false)} /> : null}
    </div>
  );
}
